import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Building2,
  Kanban,
  Settings,
  BarChart3,
  DollarSign,
  FileText,
  CalendarCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/usePermissions';
import { OpusLogo } from './OpusLogo';

interface SidebarNavProps {
  onNavigate?: () => void;
}

export function SidebarNav({ onNavigate }: SidebarNavProps) {
  const { can } = usePermissions();

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard', show: true },
    { to: '/candidates', icon: Users, label: 'Candidates', show: true },
    { to: '/jobs', icon: Briefcase, label: 'Jobs', show: true },
    { to: '/companies', icon: Building2, label: 'Companies', show: true },
    { to: '/pipeline', icon: Kanban, label: 'Pipeline', show: true },
    { to: '/placements', icon: DollarSign, label: 'Placements', show: can('placements:read') },
    { to: '/invoices', icon: FileText, label: 'Invoices', show: can('placements:read') },
    { to: '/bookings', icon: CalendarCheck, label: 'Bookings', show: true },
    { to: '/interviews', icon: CalendarCheck, label: 'Interviews', show: can('interviews:read') },
    { to: '/reports', icon: BarChart3, label: 'Reports', show: can('reports:read') },
    { to: '/settings', icon: Settings, label: 'Settings', show: true },
  ];

  return (
    <>
      {/* Logo */}
      <div className="h-16 flex items-center px-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <OpusLogo className="h-8 w-8" />
          <span className="text-lg font-semibold">Opus CRM</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6 px-3">
        <ul className="space-y-1">
          {navItems
            .filter((item) => item.show)
            .map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150',
                      isActive
                        ? 'bg-emerald-600 bg-gradient-to-b from-white/15 to-white/0 text-white shadow-[0_2px_10px_rgba(16,185,129,0.35),inset_0_1px_0_rgba(255,255,255,0.18)]'
                        : 'text-text-secondary hover:bg-white/5 hover:text-text-primary',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={cn(
                          'absolute -left-3 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-emerald-400 transition-opacity duration-150',
                          isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-40',
                        )}
                      />
                      <item.icon
                        className={cn(
                          'h-5 w-5 transition-colors',
                          !isActive && 'text-emerald-500/80 group-hover:text-emerald-300',
                        )}
                      />
                      {item.label}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
        </ul>
      </nav>

      {/* Version */}
      <div className="px-6 py-4 text-xs text-text-tertiary border-t border-white/10">
        Version 2.0.0
      </div>
    </>
  );
}

export function Sidebar() {
  return (
    <aside className="hidden lg:flex fixed left-0 top-0 h-screen w-64 bg-bg-secondary text-text-primary flex-col border-r border-white/10 z-20">
      <SidebarNav />
    </aside>
  );
}
